import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { MaterialRow, PriceChangeRow, SupplierRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatDisplayTime, formatPercent, percentToRatio } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { hq, useAuthStore } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

/** 调价单状态（`wl_material_price.approval_status`）。 */
const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  APPROVING: { text: '审批中', color: 'processing' },
  APPROVED: { text: '已生效', color: 'green' },
  REJECTED: { text: '已驳回', color: 'red' },
  CANCELED: { text: '已取消', color: 'default' }
};

/** 招标要求：调价须提前 30 天书面申请。 */
const MIN_NOTICE_DAYS = 30;

type PriceFilter = 'ALL' | 'APPROVING' | 'APPROVED' | 'REJECTED';

interface CreateForm {
  materialId: string;
  supplierId: string;
  price: number;
  taxRate: number;
  effectiveFrom: dayjs.Dayjs;
  changeReason: string;
  changeFileUrl?: string;
}

/**
 * 调价申请（招标"后勤物资采购"第 6 条）：供应商/运营方提前 1 个月书面申请，
 * 经审批（运营方复核 → 采购人确认）后生效，终审通过时自动关闭上一版价格。
 */
export default function PriceChange() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);

  const [filter, setFilter] = useState<PriceFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [materialKeyword, setMaterialKeyword] = useState('');
  const [form] = Form.useForm<CreateForm>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'price-change', 'list', filter],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.priceChange.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        approvalStatus: filter === 'ALL' ? undefined : filter
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'price-change', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.priceChange.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const materials = useQuery({
    queryKey: ['ops', 'price-change', 'materials', materialKeyword],
    placeholderData: keepPreviousData,
    queryFn: () => hq.material.list({ keyword: materialKeyword || undefined, size: 50 }),
    enabled: createOpen
  });

  const suppliers = useQuery({
    queryKey: ['ops', 'price-change', 'suppliers'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.suppliers({ size: 50 }),
    enabled: createOpen
  });

  const submit = useMutation({
    mutationFn: (values: CreateForm) => {
      const material = materialOptions.find((item) => item.id === values.materialId);
      if (!material) throw new Error('请选择物资');
      return hq.priceChange.submit({
        materialId: values.materialId,
        supplierId: values.supplierId,
        orgId: profile?.orgId,
        priceType: 'PURCHASE',
        taxKind: 'INCLUSIVE',
        // 后端 PriceChangeCreate.TaxRate 取值 0~1，表单按百分数录入，统一走 percentToRatio
        taxRate: percentToRatio(values.taxRate),
        price: Number(values.price),
        unitId: material.baseUnitId,
        minQty: 0,
        effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
        changeReason: values.changeReason,
        changeFileUrl: values.changeFileUrl || null
      });
    },
    onSuccess: async (result) => {
      message.success(`调价申请已提交：${result.approvalStatus}${result.nodeName ? `（${result.nodeName}）` : ''}`);
      setCreateOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ['ops', 'price-change'] });
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : error instanceof Error ? error.message : '提交失败');
    }
  });

  const decide = useMutation({
    mutationFn: (payload: { id: string; action: 'APPROVE' | 'REJECT'; opinion?: string }) =>
      hq.priceChange.decide(payload.id, { action: payload.action, opinion: payload.opinion }),
    onSuccess: async (result, payload) => {
      message.success(
        payload.action === 'APPROVE'
          ? `已同意${result.approvalStatus === 'APPROVED' ? '，调价已生效' : result.nodeName ? `，流转至「${result.nodeName}」` : ''}`
          : '已驳回'
      );
      await queryClient.invalidateQueries({ queryKey: ['ops', 'price-change'] });
      await detail.refetch();
    },
    onError: (error) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '审批失败');
    }
  });

  const askDecide = (row: PriceChangeRow, action: 'APPROVE' | 'REJECT') => {
    let opinion = '';
    modal.confirm({
      title: action === 'APPROVE' ? `同意调价：${row.changeNo ?? row.id}` : `驳回调价：${row.changeNo ?? row.id}`,
      content: (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Typography.Text>
            {row.materialName ?? row.materialId} 新单价 {formatAmount(row.price, 4)}（{formatDisplayDate(row.effectiveFrom)} 生效）
          </Typography.Text>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder={action === 'REJECT' ? '驳回意见（必填）' : '审批意见（可选）'}
            onChange={(event) => {
              opinion = event.target.value;
            }}
          />
        </Space>
      ),
      okText: action === 'APPROVE' ? '同意' : '驳回',
      okButtonProps: action === 'REJECT' ? { danger: true } : undefined,
      onOk: () => {
        if (action === 'REJECT' && opinion.trim().length === 0) {
          message.warning('驳回必须填写意见');
          return Promise.reject(new Error('opinion required'));
        }
        return decide.mutateAsync({ id: row.id, action, opinion: opinion.trim() || undefined });
      }
    });
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const materialOptions = materials.data?.items ?? [];
  const supplierOptions: SupplierRow[] = suppliers.data?.items ?? [];

  const columns: ColumnsType<PriceChangeRow> = [
    {
      title: '调价单号',
      dataIndex: 'changeNo',
      width: 210,
      render: (value: string | null, row) => (
        <Typography.Link onClick={() => setCurrentId(row.id)}>{value ?? row.id}</Typography.Link>
      )
    },
    {
      title: '物资',
      dataIndex: 'materialName',
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => <TipText text={value ? `${row.materialCode ?? ''} ${value}` : row.materialId} />
    },
    { title: '规格', dataIndex: 'spec', width: 130, render: (value: string | null) => value || '-' },
    { title: '新单价', dataIndex: 'price', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    {
      title: '税率',
      dataIndex: 'taxRate',
      width: 90,
      align: 'right',
      render: (value: number) => formatPercent(value)
    },
    { title: '生效日', dataIndex: 'effectiveFrom', width: 120, render: (value: string) => formatDisplayDate(value) },
    { title: '提前天数', dataIndex: 'noticeDays', width: 100, align: 'right' },
    {
      title: '状态',
      dataIndex: 'approvalStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '提交时间', dataIndex: 'applyAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            详情
          </Button>
          {row.approvalStatus === 'APPROVING' ? (
            <>
              <Button type="link" onClick={() => askDecide(row, 'APPROVE')}>
                同意
              </Button>
              <Button type="link" danger onClick={() => askDecide(row, 'REJECT')}>
                驳回
              </Button>
            </>
          ) : null}
        </Space>
      )
    }
  ];

  const price = detail.data?.price;

  return (
    <Card
      className="page-card"
      title="调价申请"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreateOpen(true);
              form.setFieldsValue({ effectiveFrom: dayjs().add(MIN_NOTICE_DAYS + 5, 'day'), taxRate: 13 });
            }}
          >
            新建调价
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        招标要求：调价须提前 {MIN_NOTICE_DAYS} 天以书面形式提交申请，经运营方复核、采购人核实同意后生效；终审通过时系统自动关闭上一版价格。
      </Typography.Paragraph>

      <Segmented<PriceFilter>
        value={filter}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '审批中', value: 'APPROVING' },
          { label: '已生效', value: 'APPROVED' },
          { label: '已驳回', value: 'REJECTED' }
        ]}
        onChange={(value) => setFilter(value)}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="调价申请加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<PriceChangeRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无调价申请' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={price?.changeNo ?? '调价申请详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={880}
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : price ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_META[price.approvalStatus]?.color ?? 'default'}>
                  {STATUS_META[price.approvalStatus]?.text ?? price.approvalStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="物资">
                {price.materialName ?? price.materialId}（{price.materialCode ?? '-'}）
              </Descriptions.Item>
              <Descriptions.Item label="规格">{price.spec || '-'}</Descriptions.Item>
              <Descriptions.Item label="供应商">
                {price.supplierName ?? `供应商 ${price.supplierId}`}
              </Descriptions.Item>
              <Descriptions.Item label="新单价">{formatAmount(price.price, 4)}</Descriptions.Item>
              <Descriptions.Item label="税率">{formatPercent(price.taxRate)}</Descriptions.Item>
              <Descriptions.Item label="生效日">{formatDisplayDate(price.effectiveFrom)}</Descriptions.Item>
              <Descriptions.Item label="提前天数">{price.noticeDays} 天</Descriptions.Item>
              <Descriptions.Item label="提交时间">{formatDisplayTime(price.applyAt)}</Descriptions.Item>
              <Descriptions.Item label="终审时间">{formatDisplayTime(price.approvedAt)}</Descriptions.Item>
              <Descriptions.Item label="调价原因" span={2}>
                {price.changeReason || '-'}
              </Descriptions.Item>
            </Descriptions>

            {price.approvalStatus === 'APPROVING' ? (
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  loading={decide.isPending}
                  onClick={() => askDecide(price, 'APPROVE')}
                >
                  同意
                </Button>
                <Button danger loading={decide.isPending} onClick={() => askDecide(price, 'REJECT')}>
                  驳回
                </Button>
              </Space>
            ) : null}

            <Typography.Title level={5}>审批轨迹</Typography.Title>
            {detail.data?.logs?.length ? (
              <Timeline
                items={detail.data.logs.map((log) => ({
                  color: log.to_status === 'REJECTED' ? 'red' : log.to_status === 'APPROVED' ? 'green' : 'blue',
                  children: (
                    <Space direction="vertical" size={0}>
                      <Typography.Text>
                        {log.action} · {log.node_code ?? '-'} → {log.to_status ?? '-'}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {log.actor_name ?? '-'} {formatDisplayTime(log.act_at)} {log.opinion ? `：${log.opinion}` : ''}
                      </Typography.Text>
                    </Space>
                  )
                }))}
              />
            ) : (
              <Typography.Text type="secondary">尚未进入审批</Typography.Text>
            )}
          </>
        ) : null}
      </Drawer>

      <Modal
        title="新建调价申请"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
        width={720}
        confirmLoading={submit.isPending}
        onOk={async () => {
          const values = await form.validateFields();
          submit.mutate(values);
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="materialId" label="物资" rules={[{ required: true, message: '请选择物资' }]}>
            <Select
              showSearch
              filterOption={false}
              placeholder="按名称/编码搜索"
              onSearch={(value) => setMaterialKeyword(value)}
              options={materialOptions.map((item: MaterialRow) => ({
                value: item.id,
                label: `${item.materialCode ?? ''} ${item.materialName ?? ''} ${item.spec ?? ''}`
              }))}
            />
          </Form.Item>
          <Form.Item name="supplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="选择供应商"
              options={supplierOptions.map((item: SupplierRow) => ({
                value: item.id,
                label: `${item.supplierCode} ${item.supplierName}`
              }))}
            />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="price" label="新单价（含税）" rules={[{ required: true, message: '请填写新单价' }]}>
              <InputNumber min={0.0001} precision={4} style={{ width: 180 }} addonBefore="¥" />
            </Form.Item>
            <Form.Item name="taxRate" label="税率（%）" rules={[{ required: true, message: '请填写税率' }]}>
              <InputNumber min={0} max={100} precision={0} style={{ width: 140 }} addonAfter="%" />
            </Form.Item>
            <Form.Item
              name="effectiveFrom"
              label="生效日"
              rules={[{ required: true, message: '请选择生效日' }]}
              extra={`须晚于今天 ${MIN_NOTICE_DAYS} 天`}
            >
              <DatePicker
                style={{ width: 180 }}
                disabledDate={(current) => current && current < dayjs().add(MIN_NOTICE_DAYS, 'day').startOf('day')}
              />
            </Form.Item>
          </Space>
          <Form.Item
            name="changeReason"
            label="调价原因"
            rules={[
              { required: true, message: '请填写调价原因' },
              { min: 5, message: '不少于 5 个字，需与书面申请一致' }
            ]}
          >
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="changeFileUrl" label="书面申请附件 URL（可选）">
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
